import React, { useState } from "react";
import { useDocumentEditor } from "../onboardingHooks/useDocumentEditor";
import Button from "../../Buttons/Button";

const SIGNATURE_FONTS = [
  { name: "poppins", family: "Poppins, cursive" },
  { name: "Roboto", family: "Roboto, cursive" },
];

// Helper to get field value for a specific user - SIMPLIFIED
export const getFieldValueForUser = (field, userId) => {
  // Fallback for string values
  return field.value || "";
};

export const getDisplayValue = (field, value, mode, userId, userRole) => {
  // In fill mode, use the passed value (which should be user-specific)
  if (mode === "fill" && userId) {
    return getFieldValueForUser(field, userId);
  }

  // In preview/editor mode, show the first user's value
  if (field.value && typeof field.value === "object") {
    const users = Object.keys(field.value);
    if (users.length > 0) {
      return field.value[users[0]];
    }
  }

  return field.value || "";
};

const bufferToImageUrl = (bufferData) => {
  if (!bufferData) {
    return null;
  }

  try {
    const uint8Array = new Uint8Array(bufferData);
    const blob = new Blob([uint8Array]);
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error("Error converting buffer to image URL:", error);
    return null;
  }
};

// Helper function to check if value is buffer format
const isBufferFormat = (value) => {
  return value && Array.isArray(value);
};

export const FieldRenderer = ({
  field,
  value,
  onChange,
  readOnly = false,
  mode = "fill", // 'fill' | 'preview' | 'editor'
  userId = null, // Current user ID
  userRole = null, // Current user role
}) => {
  const isEditable = !readOnly && mode === "fill";
  const { calculateMaxChars } = useDocumentEditor();
  const [showFontSelector, setShowFontSelector] = useState(false);
  const [selectedFont, setSelectedFont] = useState(SIGNATURE_FONTS[0]);
  const [signatureText, setSignatureText] = useState("");

  const baseInputClass = `border rounded px-2 py-1 text-sm ${
    isEditable ? "bg-white" : "bg-gray-100 cursor-not-allowed"
  }`;

  // Get the appropriate display value
  const displayValue = getDisplayValue(field, value, mode, userId, userRole);

  const renderLabel = () => {
    if (field.showLabel === false) return null;
    return (
      <div className="text-xs font-semibold mb-1">
        {field.label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
        {/* Show who filled this field in preview mode */}
        {mode === "preview" &&
          field.value &&
          typeof field.value === "object" && (
            <span className="text-gray-500 text-[10px] ml-1">
              ({Object.keys(field.value).length} user(s) filled)
            </span>
          )}
      </div>
    );
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const bufferArray = Array.from(uint8Array);
        onChange?.(bufferArray);
      } catch (error) {
        console.error("Error reading image file:", error);
      }
    }
  };

  const handleFontSelect = (font) => {
    setSelectedFont(font);
    setShowFontSelector(false);
    // Apply the signature with selected font
    if (signatureText) {
      onChange?.({
        text: signatureText,
        fontFamily: font.family,
      });
    }
  };

  const handleSignatureChange = (text) => {
    setSignatureText(text);
    onChange?.({
      text: text,
      fontFamily: selectedFont.family,
    });
  };

  const renderField = () => {
    if (!isEditable && displayValue) {
      switch (field.type) {
        case "checkbox":
          return (
            <div className="flex items-center">
              <span
                className="text-sm"
                style={{
                  fontSize: `${field.fontSize}px` || "14px",
                  fontFamily: field.fontFamily || "Arial",
                  color: field.fontColor || "#000000",
                }}
              >
                {displayValue === true || displayValue === "true" ? "☑" : "☐"}
              </span>
            </div>
          );

        case "signature":
          const sigText =
            typeof displayValue === "object" ? displayValue.text : displayValue;
          const sigFont =
            typeof displayValue === "object"
              ? displayValue.fontFamily
              : "cursive";
          return (
            <div
              className="text-sm"
              style={{
                fontSize: `${field.fontSize}px` || "10px",
                fontFamily: sigFont,
                color: field.fontColor || "#000000",
                minWidth: field.width || 200,
              }}
            >
              {sigText}
            </div>
          );

        case "image":
          return displayValue ? (
            <img
              src={displayValue}
              alt={field.alt || "Image"}
              className="object-contain"
              style={{
                width: field.width || 150,
                height: field.height || 100,
              }}
            />
          ) : null;

        default:
          return (
            <div
              className="text-sm"
              style={{
                fontSize: `${field.fontSize}px` || "14px",
                fontFamily: field.fontFamily || "Arial",
                color: field.fontColor || "#000000",
                minWidth: field.width || 200,
              }}
            >
              {displayValue}
            </div>
          );
      }
    }

    switch (field.type) {
      case "checkbox":
        return (
          <input
            type="checkbox"
            className="w-4 h-4"
            checked={displayValue === true || displayValue === "true"}
            onChange={(e) => onChange?.(e.target.checked)}
            disabled={!isEditable}
          />
        );

      case "text":
      case "name":
      case "email":
      case "phone":
        return (
          <input
            type="text"
            className={baseInputClass}
            style={{
              width: field.width || 200,
              fontSize: `${field.fontSize}px` || "14px",
              fontFamily: field.fontFamily || "Arial",
              color: field.fontColor || "#000000",
            }}
            value={displayValue || ""}
            onChange={(e) => onChange?.(e.target.value)}
            readOnly={!isEditable}
            placeholder={isEditable ? field.label : ""}
            maxLength={calculateMaxChars(
              field.width || 200,
              field.fontSize || 14
            )}
          />
        );

      case "date":
        return (
          <input
            type="date"
            className={baseInputClass}
            style={{
              width: field.width || 200,
              fontSize: `${field.fontSize}px` || "14px",
              fontFamily: field.fontFamily || "Arial",
              color: field.fontColor || "#000000",
            }}
            value={displayValue || ""}
            onChange={(e) => onChange?.(e.target.value)}
            readOnly={!isEditable}
          />
        );

      case "select":
        return (
          <select
            className={baseInputClass}
            style={{
              width: field.width || 200,
              fontSize: `${field.fontSize}px` || "14px",
              fontFamily: field.fontFamily || "Arial",
              color: field.fontColor || "#000000",
            }}
            value={displayValue || ""}
            onChange={(e) => onChange?.(e.target.value)}
            disabled={!isEditable}
          >
            <option value="">Select...</option>
            {(field.options || []).map((opt, i) => (
              <option key={i} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        );

      case "signature":
        const currentText =
          typeof displayValue === "object"
            ? displayValue.text
            : displayValue || "";
        const currentFont =
          typeof displayValue === "object"
            ? displayValue.fontFamily
            : selectedFont.family;

        return (
          <div className="relative">
            <div className="flex items-center gap-2">
              <input
                type="text"
                className={baseInputClass}
                style={{
                  width: field.width || 200,
                  fontSize: `${field.fontSize}px` || "14px",
                  fontFamily: currentFont,
                  color: field.fontColor || "#000000",
                }}
                value={currentText}
                onChange={(e) => handleSignatureChange(e.target.value)}
                readOnly={!isEditable}
                placeholder={isEditable ? "Sign here and select" : ""}
              />
              {isEditable && (
                <Button
                  onClick={() => setShowFontSelector(!showFontSelector)}
                  icon={"text_fields"}
                  variant="outlined"
                  type="icon"
                  color="black"
                  className="bg-gray-200 !rounded-sm !px-7"
                  size="sm"
                  cla
                />
              )}
            </div>

            {showFontSelector && isEditable && (
              <div className="absolute top-full left-0 mt-1 bg-white border rounded shadow-lg z-50 min-w-[200px]">
                <div className="p-2 border-b bg-gray-50 text-xs font-semibold">
                  Choose Signature Style
                </div>
                {SIGNATURE_FONTS.map((font, idx) => (
                  <Button
                    title={
                      <>
                        <div className="text-xs text-gray-500 ">
                          {font.name}
                        </div>
                        <div
                          style={{
                            fontFamily: font.family,
                            fontSize: "14px",
                            color: "#000",
                          }}
                        >
                          {currentText || "Your Signature"}
                        </div>
                      </>
                    }
                    onClick={() => handleFontSelect(font)}
                    variant="text"
                    color="black"
                    className="w-full border-b !rounded-none !py-1"
                  />
                ))}
              </div>
            )}
          </div>
        );

      case "image":
        // Convert buffer format to displayable URL if needed
        let imageUrl = null;
        if (isBufferFormat(displayValue)) {
          imageUrl = bufferToImageUrl(displayValue);
        } else if (
          typeof displayValue === "string" &&
          displayValue.startsWith("blob:")
        ) {
          imageUrl = displayValue;
        } else if (typeof displayValue === "string" && displayValue) {
          // For regular URLs (http/https)
          imageUrl = displayValue;
        }

        return (
          <div
            className="border rounded flex flex-col items-center justify-center gap-2 p-2 text-xs"
            style={{
              width: field.width || 150,
              height: field.height || 100,
            }}
          >
            {imageUrl ? (
              <>
                <img
                  src={imageUrl}
                  alt={field.alt || "Uploaded"}
                  className="w-full h-full object-contain rounded"
                />
                {isEditable && (
                  <label className="text-[11px] text-info-600 cursor-pointer hover:underline">
                    Change Image
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </>
            ) : isEditable ? (
              <label className="flex flex-col items-center justify-center cursor-pointer text-info-600 hover:underline">
                Upload Image
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            ) : (
              <span className="text-gray-400 text-xs">No image</span>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const shouldApplyPosition = mode === "editor";

  return (
    <div
      className={`bg-transparent rounded p-2 ${
        shouldApplyPosition ? "absolute" : ""
      }`}
      style={shouldApplyPosition ? { left: field.x, top: field.y } : {}}
    >
      {field.labelPosition === "top" && renderLabel()}
      <div className="flex items-center gap-2">
        {field.labelPosition === "left" && (
          <div className="text-xs font-semibold whitespace-nowrap">
            {field.label}
            {field.required && <span className="text-red-500 ml-0.5">*</span>}:
          </div>
        )}
        {renderField()}
      </div>
    </div>
  );
};
